import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  AssignmentSchema,
  BillingCheckoutSessionSchema,
  BillingPortalSessionSchema,
  CreateAssignmentInputSchema,
  ListAssignmentsResponseSchema,
  DocumentListSchema,
  DocumentSchema,
  CreateDocumentInputSchema,
  UpdateDocumentInputSchema,
  ShareDocumentSchema,
  ShareWithProgressionSchema,
  UpdateAssignmentInputSchema,
  UsageSummarySchema,
  ClassResponseSchema,
  CreateClassRequestSchema,
  UpdateClassRequestSchema,
  ListClassesResponseSchema,
  StudentResponseSchema,
  CreateStudentRequestSchema,
  UpdateStudentRequestSchema,
  ListStudentsResponseSchema,
  CriteriaTemplateListSchema,
  CriteriaTemplateWithCriteriaSchema,
  CriteriaTemplateSchema,
  CreateCriteriaTemplateInputSchema,
  UpdateCriteriaTemplateInputSchema,
  ApplyCriteriaTemplateInputSchema,
  SaveAsCriteriaTemplateInputSchema,
  AssignmentCriteriaSchema,
  EmailTemplateSchema,
  EmailTemplateListSchema,
  CreateEmailTemplateInputSchema,
  UpdateEmailTemplateInputSchema,
  RenderEmailResponseSchema,
  StudentPortalResponseSchema,
  PortalProgressionResponseSchema,
  PortalAssignmentWritingResponseSchema,
  PortalCreateDocumentResponseSchema,
  PortalDocumentVersionSchema,
  PortalVersionSubmitResponseSchema,
} from "@markeroo/shared";
import type {
  Assignment,
  AssignmentStatus,
  AuthUser,
  BillingCheckoutSession,
  BillingPortalSession,
  CreateAssignmentInput,
  CreateDocumentInput,
  Document,
  DocumentList,
  PaginatedAssignments,
  ShareDocument,
  ShareWithProgression,
  UpdateDocumentInput,
  UpdateAssignmentInput,
  UsageSummary,
  Class,
  CreateClassInput,
  UpdateClassInput,
  PaginatedClasses,
  Student,
  CreateStudentInput,
  UpdateStudentInput,
  PaginatedStudents,
  CriteriaTemplateWithCriteria,
  CriteriaTemplateList,
  CreateCriteriaTemplateInput,
  UpdateCriteriaTemplateInput,
  ApplyCriteriaTemplateInput,
  SaveAsCriteriaTemplateInput,
  PaperType,
  EmailTemplate,
  EmailTemplateList,
  CreateEmailTemplateInput,
  UpdateEmailTemplateInput,
  RenderEmailResponse,
  StudentPortalResponse,
  UpdateCompletedHighlightsInput,
  PortalProgressionResponse,
  PortalAssignmentWritingResponse,
  PortalCreateDocumentResponse,
  PortalDocumentVersion,
  PortalVersionUpdateInput,
  PortalVersionSubmitResponse,
  GCCourse,
  GCClassOut,
  GCSyncResult,
} from "@markeroo/shared";

import type {
  AllGradesVocabularyResponse,
  GradeVocabularyResponse,
  UpdateGradeVocabularyRequest,
} from "./vocabularyTypes";

import type {
  EditMetricsResponse,
  ExtensionDocumentListResponse,
  ExtensionDocumentDetailResponse,
  ExtensionEditEventsResponse,
  ExtensionLicenseStatusResponse,
} from "./client";

import { useApiClient } from "../providers/ApiClientProvider";
import { useAuthStore } from "../stores/auth";

const assignmentsQueryKey = ["assignments"] as const;
const assignmentQueryKey = (assignmentId: string) => ["assignment", assignmentId] as const;
const usageQueryKey = ["usage"] as const;
const pollStatuses: AssignmentStatus[] = ["queued", "processing"];

const shouldPollAssignment = (assignment?: Assignment | null): boolean => {
  if (!assignment || !assignment.status) {
    return false;
  }

  // Poll for queued/processing statuses
  return pollStatuses.includes(assignment.status);
};

const shouldPollDocuments = (documents?: DocumentList | null, assignment?: Assignment | null): boolean => {
  // Poll if assignment is being processed
  if (shouldPollAssignment(assignment)) {
    return true;
  }

  // Also poll if any documents don't have assessments yet
  // This handles the case where a document was just saved and is being processed,
  // but the assignment status has already changed from "processing"
  if (documents?.items) {
    return documents.items.some((doc: Document) => !doc.hasAssessment);
  }

  return false;
};

const shouldPollAssignments = (result?: PaginatedAssignments): boolean => {
  if (!result || !Array.isArray(result.items)) {
    return false;
  }
  return result.items.some((item) => shouldPollAssignment(item));
};

export function useAssignments(includeArchived = false) {
  const api = useApiClient();
  return useQuery<PaginatedAssignments>({
    queryKey: [...assignmentsQueryKey, includeArchived],
    queryFn: async () => {
      // Fetch all pages to get all assignments (API defaults to 20 per page)
      const allItems: Assignment[] = [];
      let cursor: string | undefined = undefined;

      do {
        const data = await api.listAssignments({
          include_archived: includeArchived,
          cursor,
          limit: 100, // Max allowed per request
        });
        const parsed = ListAssignmentsResponseSchema.parse(data);
        allItems.push(...parsed.items);
        cursor = parsed.nextCursor ?? undefined;
      } while (cursor);

      return { items: allItems, nextCursor: null };
    },
    refetchInterval: (query) => {
      const data = query.state.data;
      return shouldPollAssignments(data) ? 5000 : false;
    },
    refetchIntervalInBackground: true,
  });
}

const classAssignmentsQueryKey = (classId: string, includeArchived: boolean) =>
  ["class-assignments", classId, includeArchived] as const;

export function useClassAssignments(classId: string | undefined, includeArchived = false) {
  const api = useApiClient();

  const result = useQuery<Assignment[]>({
    queryKey: classId ? classAssignmentsQueryKey(classId, includeArchived) : ["class-assignments"],
    queryFn: async () => {
      if (!classId) {
        throw new Error("classId required");
      }

      // Fetch all assignments for this class via server-side filtering
      // Loop through pages since a class could theoretically have 100+ assignments
      const allItems: Assignment[] = [];
      let cursor: string | undefined = undefined;

      do {
        const data = await api.listAssignments({
          include_archived: includeArchived,
          class_id: classId,
          cursor,
          limit: 100,
        });
        const parsed = ListAssignmentsResponseSchema.parse(data);
        allItems.push(...parsed.items);
        cursor = parsed.nextCursor ?? undefined;
      } while (cursor);

      return allItems;
    },
    enabled: Boolean(classId),
    refetchInterval: (query) => {
      const data = query.state.data;
      // Poll if any assignments are being processed
      return data?.some((a) => shouldPollAssignment(a)) ? 5000 : false;
    },
    refetchIntervalInBackground: true,
  });

  return {
    data: result.data ?? [],
    isLoading: result.isLoading,
    isError: result.isError,
    refetch: result.refetch,
  };
}

export function useAssignment(assignmentId: string | undefined) {
  const api = useApiClient();
  return useQuery<Assignment>({
    queryKey: assignmentId ? assignmentQueryKey(assignmentId) : ["assignment"],
    queryFn: async () => {
      if (!assignmentId) {
        throw new Error("assignmentId required");
      }
      const data = await api.getAssignment(assignmentId);
      try {
        return AssignmentSchema.parse(data);
      } catch (error) {
        console.error("Zod validation error in useAssignment:", error);
        console.error("Failed to validate data:", JSON.stringify(data, null, 2));
        throw error;
      }
    },
    enabled: Boolean(assignmentId),
    refetchInterval: (query) => {
      const data = query.state.data;
      return shouldPollAssignment(data) ? 5000 : false;
    },
    refetchIntervalInBackground: true,
  });
}

export function useCreateAssignment() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateAssignmentInput) => {
      const parsed = CreateAssignmentInputSchema.parse(payload);
      const result = await api.createAssignment(parsed);
      return AssignmentSchema.parse(result);
    },
    onSuccess: async (assignment) => {
      const updateAssignmentsCache = (includeArchived: boolean) => {
        queryClient.setQueryData(
          [...assignmentsQueryKey, includeArchived],
          (current: PaginatedAssignments | undefined) => {
            if (!current) {
              return current;
            }
            const existingItems = Array.isArray(current.items) ? current.items : [];
            const filtered = existingItems.filter((item) => item.id !== assignment.id);
            return {
              ...current,
              items: [assignment, ...filtered],
            };
          }
        );
      };

      updateAssignmentsCache(true);
      updateAssignmentsCache(false);
      await Promise.all([
        queryClient.refetchQueries({ queryKey: assignmentsQueryKey }),
        queryClient.refetchQueries({ queryKey: usageQueryKey }),
        // Invalidate all class-assignments caches
        queryClient.invalidateQueries({ queryKey: ["class-assignments"] }),
      ]);
    },
  });
}

export function useUpdateAssignment() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateAssignmentInput }) => {
      const parsed = UpdateAssignmentInputSchema.parse(payload);
      const result = await api.updateAssignment(id, parsed);
      return AssignmentSchema.parse(result);
    },
    onSuccess: async (result, variables) => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: assignmentsQueryKey }),
        queryClient.refetchQueries({ queryKey: assignmentQueryKey(variables.id) }),
        queryClient.refetchQueries({ queryKey: documentsQueryKey(variables.id) }),
        queryClient.refetchQueries({ queryKey: usageQueryKey }),
        // Invalidate all class-assignments caches (assignment may have moved between classes)
        queryClient.invalidateQueries({ queryKey: ["class-assignments"] }),
      ]);
    },
  });
}

export function useDeleteAssignment() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.deleteAssignment(id);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: assignmentsQueryKey }),
        queryClient.refetchQueries({ queryKey: usageQueryKey }),
        // Invalidate all class-assignments caches since we don't know which class
        queryClient.invalidateQueries({ queryKey: ["class-assignments"] }),
      ]);
    },
  });
}

export function useDeleteAssignmentForever() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.deleteAssignmentForever(id);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: assignmentsQueryKey }),
        // Invalidate all class-assignments caches since we don't know which class
        queryClient.invalidateQueries({ queryKey: ["class-assignments"] }),
      ]);
    },
  });
}

export function useAssessDocument() {
  const api = useApiClient();
  return useMutation({
    mutationFn: async ({ assignmentId, documentId }: { assignmentId: string; documentId: string }): Promise<void> => {
      await api.assessDocument(assignmentId, documentId);
    },
  });
}

const documentsQueryKey = (assignmentId: string) => ["assignment-documents", assignmentId] as const;
const shareDocumentQueryKey = (token: string) => ["share-document", token] as const;

export function useAssignmentDocuments(assignmentId: string, assignment?: Assignment) {
  const api = useApiClient();
  return useQuery({
    queryKey: documentsQueryKey(assignmentId),
    queryFn: async () => {
      const data = await api.listAssignmentDocuments(assignmentId);
      try {
        return DocumentListSchema.parse(data);
      } catch (error) {
        console.error("Zod validation error in useAssignmentDocuments:", error);
        console.error("Failed to validate data:", JSON.stringify(data, null, 2));
        throw error;
      }
    },
    enabled: Boolean(assignmentId),
    refetchInterval: (query) => {
      const documents = query.state.data;
      return shouldPollDocuments(documents, assignment) ? 5000 : false;
    },
    refetchIntervalInBackground: true,
  });
}

export function useUsageSummary() {
  const api = useApiClient();
  return useQuery<UsageSummary>({
    queryKey: usageQueryKey,
    queryFn: async () => {
      const data = await api.getUsage();
      return UsageSummarySchema.parse(data);
    },
    staleTime: 60_000,
  });
}

export function useCreateCheckoutSession() {
  const api = useApiClient();
  return useMutation<BillingCheckoutSession, Error, "monthly" | "annual" | undefined>({
    mutationFn: async (plan?: "monthly" | "annual") => {
      const result = await api.createCheckoutSession(plan ?? "monthly");
      return BillingCheckoutSessionSchema.parse(result);
    },
  });
}

export function useCreatePortalSession() {
  const api = useApiClient();
  return useMutation<BillingPortalSession>({
    mutationFn: async () => {
      const result = await api.createPortalSession();
      return BillingPortalSessionSchema.parse(result);
    },
  });
}

export function useUpgradeToAnnual() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean; message: string }>({
    mutationFn: async () => {
      return await api.upgradeToAnnual();
    },
    onSuccess: async () => {
      // Refetch usage to get updated billing interval
      await queryClient.refetchQueries({ queryKey: usageQueryKey });
    },
  });
}

export function useShareDocument(token: string | undefined) {
  const api = useApiClient();
  return useQuery<ShareWithProgression>({
    queryKey: token ? shareDocumentQueryKey(token) : ["share-document"],
    queryFn: async () => {
      if (!token) {
        throw new Error("token required");
      }
      const data = await api.getSharedDocument(token);
      try {
        return ShareWithProgressionSchema.parse(data);
      } catch (error) {
        console.error("Zod validation error in useShareDocument:", error);
        console.error("Failed to validate data:", JSON.stringify(data, null, 2));
        throw error;
      }
    },
    enabled: Boolean(token),
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | undefined)?.status;
      if (status && [404, 410].includes(status)) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

export function useCreateDocument(assignmentId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Document, unknown, CreateDocumentInput>({
    mutationFn: async (payload: CreateDocumentInput) => {
      const parsed = CreateDocumentInputSchema.parse(payload);
      const result = await api.createDocument(assignmentId, parsed);
      return DocumentSchema.parse(result);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: documentsQueryKey(assignmentId) }),
        queryClient.refetchQueries({ queryKey: assignmentsQueryKey }),
        queryClient.refetchQueries({ queryKey: usageQueryKey }),
      ]);
    },
  });
}

export function useUpdateDocument(assignmentId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Document, unknown, { documentId: string; payload: UpdateDocumentInput }>({
    mutationFn: async ({ documentId, payload }) => {
      const parsed = UpdateDocumentInputSchema.parse(payload);
      const result = await api.updateDocument(assignmentId, documentId, parsed);
      return DocumentSchema.parse(result);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: documentsQueryKey(assignmentId) }),
        queryClient.refetchQueries({ queryKey: assignmentsQueryKey }),
      ]);
    },
  });
}

export function useDeleteDocument(assignmentId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: async (documentId: string) => {
      await api.deleteDocument(assignmentId, documentId);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: documentsQueryKey(assignmentId) }),
        queryClient.refetchQueries({ queryKey: assignmentsQueryKey }),
      ]);
    },
  });
}

export function useCreateShareLink(assignmentId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Document, unknown, string>({
    mutationFn: async (documentId: string) => {
      const result = await api.createShareLink(assignmentId, documentId);
      return DocumentSchema.parse(result);
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: documentsQueryKey(assignmentId) });
    },
  });
}

export function useRevokeShareLink(assignmentId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Document, unknown, string>({
    mutationFn: async (documentId: string) => {
      const result = await api.revokeShareLink(assignmentId, documentId);
      return DocumentSchema.parse(result);
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: documentsQueryKey(assignmentId) });
    },
  });
}

export function useDocumentMarkup(assignmentId: string, documentId: string | null) {
  const api = useApiClient();
  return useQuery<ShareDocument>({
    queryKey: ["assignments", assignmentId, "documents", documentId, "markup"],
    queryFn: async () => {
      if (!documentId) {
        throw new Error("Document ID is required");
      }
      const result = await api.getDocumentMarkup(assignmentId, documentId);
      try {
        return ShareDocumentSchema.parse(result);
      } catch (error) {
        console.error("Zod validation error in useDocumentMarkup:", error);
        console.error("Failed to validate data:", JSON.stringify(result, null, 2));
        throw error;
      }
    },
    enabled: !!documentId,
  });
}

export function useDeleteHighlight(assignmentId: string, documentId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const markupQueryKey = ["assignments", assignmentId, "documents", documentId, "markup"] as const;

  return useMutation<ShareDocument, unknown, string, { previousMarkup?: ShareDocument }>({
    mutationFn: async (highlightId: string) => {
      return await api.deleteDocumentHighlight(assignmentId, documentId, highlightId);
    },
    onMutate: async (highlightId: string) => {
      // Cancel outgoing refetches to prevent race conditions
      await queryClient.cancelQueries({ queryKey: markupQueryKey });

      // Snapshot previous value for rollback
      const previousMarkup = queryClient.getQueryData<ShareDocument>(markupQueryKey);

      // Optimistically update cache by adding to removedLabelIds
      if (previousMarkup) {
        const currentRemoved = previousMarkup.removedLabelIds || [];
        queryClient.setQueryData<ShareDocument>(markupQueryKey, {
          ...previousMarkup,
          removedLabelIds: [...currentRemoved, highlightId],
        });
      }

      // Return context for rollback
      return { previousMarkup };
    },
    onError: (_error, _highlightId, context) => {
      // Rollback on error
      if (context?.previousMarkup) {
        queryClient.setQueryData(markupQueryKey, context.previousMarkup);
      }
    },
    onSuccess: (data) => {
      // Update cache with server response (optimistic update confirmed)
      queryClient.setQueryData<ShareDocument>(markupQueryKey, data);
    },
  });
}

export function useUpdateShowAllMarkup(assignmentId: string, documentId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const markupQueryKey = ["assignments", assignmentId, "documents", documentId, "markup"] as const;

  return useMutation<ShareDocument, unknown, boolean>({
    mutationFn: async (showAllMarkup: boolean) => {
      return await api.updateDocumentShowAllMarkup(assignmentId, documentId, showAllMarkup);
    },
    onMutate: async () => {
      // Cancel outgoing refetches to prevent race conditions
      await queryClient.cancelQueries({ queryKey: markupQueryKey });
    },
    onSuccess: (data) => {
      // Update cache with server response
      queryClient.setQueryData<ShareDocument>(markupQueryKey, data);
    },
  });
}

export function useCreateTeacherComment(assignmentId: string, documentId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const markupQueryKey = ["assignments", assignmentId, "documents", documentId, "markup"] as const;

  return useMutation({
    mutationFn: async (payload: {
      start_path: number[];
      start_char: number;
      end_path: number[];
      end_char: number;
      comment_text: string;
      category: string;
      phrase: string;
      before_context?: string | null;
      after_context?: string | null;
    }) => {
      return await api.createTeacherComment(assignmentId, documentId, payload);
    },
    onMutate: async () => {
      // Cancel outgoing refetches to prevent race conditions
      await queryClient.cancelQueries({ queryKey: markupQueryKey });
    },
    onSuccess: (data) => {
      // Immediately update cache with server response (includes new comment)
      queryClient.setQueryData<ShareDocument>(markupQueryKey, data);
    },
  });
}

export function useUpdateTeacherComment(assignmentId: string, documentId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const markupQueryKey = ["assignments", assignmentId, "documents", documentId, "markup"] as const;

  return useMutation({
    mutationFn: async ({ commentId, payload }: {
      commentId: string;
      payload: { comment_text: string; category?: string };
    }) => {
      return await api.updateTeacherComment(assignmentId, documentId, commentId, payload);
    },
    onMutate: async () => {
      // Cancel outgoing refetches to prevent race conditions
      await queryClient.cancelQueries({ queryKey: markupQueryKey });
    },
    onSuccess: (data) => {
      // Immediately update cache with server response (includes updated comment)
      queryClient.setQueryData<ShareDocument>(markupQueryKey, data);
    },
  });
}

export function useDeleteTeacherComment(assignmentId: string, documentId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const markupQueryKey = ["assignments", assignmentId, "documents", documentId, "markup"] as const;

  return useMutation({
    mutationFn: async (commentId: string) => {
      return await api.deleteTeacherComment(assignmentId, documentId, commentId);
    },
    onMutate: async (commentId: string) => {
      // Optimistic update: immediately remove comment
      await queryClient.cancelQueries({ queryKey: markupQueryKey });

      const previousMarkup = queryClient.getQueryData<ShareDocument>(markupQueryKey);

      if (previousMarkup) {
        const filteredComments = (previousMarkup.teacherComments || []).filter(
          (comment) => comment.id !== commentId
        );
        queryClient.setQueryData<ShareDocument>(markupQueryKey, {
          ...previousMarkup,
          teacherComments: filteredComments,
        });
      }

      return { previousMarkup };
    },
    onError: (_error, _commentId, context) => {
      // Rollback on error
      if (context?.previousMarkup) {
        queryClient.setQueryData(markupQueryKey, context.previousMarkup);
      }
    },
    onSuccess: (data) => {
      // Update cache with server response (optimistic update confirmed)
      queryClient.setQueryData<ShareDocument>(markupQueryKey, data);
    },
  });
}

export function useDeleteCriterion(assignmentId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: async (criterionKey: string) => {
      await api.deleteCriterion(assignmentId, criterionKey);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: assignmentQueryKey(assignmentId) }),
        queryClient.refetchQueries({ queryKey: assignmentsQueryKey }),
        queryClient.refetchQueries({ queryKey: documentsQueryKey(assignmentId) }),
      ]);
    },
  });
}

export function useCreateCriterion(assignmentId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { label: string; llmDescription: string; humanDescription: string; category?: string }) => {
      return await api.createCriterion(assignmentId, payload);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: assignmentQueryKey(assignmentId) }),
        queryClient.refetchQueries({ queryKey: assignmentsQueryKey }),
        queryClient.refetchQueries({ queryKey: documentsQueryKey(assignmentId) }),
      ]);
    },
  });
}

export function useUpdateCriterion(assignmentId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      criterionKey: string;
      label?: string;
      llmDescription?: string;
      humanDescription?: string;
      category?: string;
    }) => {
      const { criterionKey, ...rest } = payload;
      return await api.updateCriterion(assignmentId, criterionKey, rest);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: assignmentQueryKey(assignmentId) }),
        queryClient.refetchQueries({ queryKey: assignmentsQueryKey }),
        queryClient.refetchQueries({ queryKey: documentsQueryKey(assignmentId) }),
      ]);
    },
  });
}

export function useGenerateCriteria(assignmentId: string) {
  const api = useApiClient();
  return useMutation({
    mutationFn: async (payload: { prompt: string; gradeLevel?: number | null }) => {
      return await api.generateCriteria(assignmentId, payload);
    },
  });
}

export function useGetStreamingToken() {
  const api = useApiClient();
  return useMutation<{ token: string; expiresIn: number }, Error>({
    mutationFn: async (): Promise<{ token: string; expiresIn: number }> => {
      return await api.getStreamingToken();
    },
  });
}

export function useBulkCreateCriteria(assignmentId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (criteria: Array<{
      label: string;
      llmDescription: string;
      humanDescription: string;
    }>) => {
      return await api.bulkCreateCriteria(assignmentId, { criteria });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: assignmentQueryKey(assignmentId) }),
        queryClient.refetchQueries({ queryKey: assignmentsQueryKey }),
        queryClient.refetchQueries({ queryKey: documentsQueryKey(assignmentId) }),
      ]);
    },
  });
}

export function useResetCriteriaDefaults(assignmentId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      return await api.resetCriteriaDefaults(assignmentId);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: assignmentQueryKey(assignmentId) }),
        queryClient.refetchQueries({ queryKey: assignmentsQueryKey }),
        queryClient.refetchQueries({ queryKey: documentsQueryKey(assignmentId) }),
      ]);
    },
  });
}

const classesQueryKey = ["classes"] as const;
const classQueryKey = (classId: string) => ["class", classId] as const;
const studentsQueryKey = (classId: string) => ["class-students", classId] as const;
const studentQueryKey = (classId: string, studentId: string) =>
  ["class", classId, "student", studentId] as const;

export function useClasses() {
  const api = useApiClient();
  return useQuery<PaginatedClasses>({
    queryKey: classesQueryKey,
    queryFn: async () => {
      const data = await api.listClasses();
      return ListClassesResponseSchema.parse(data);
    },
  });
}

export function useClass(classId: string | undefined) {
  const api = useApiClient();
  return useQuery<Class>({
    queryKey: classId ? classQueryKey(classId) : ["class"],
    queryFn: async () => {
      if (!classId) {
        throw new Error("classId required");
      }
      const data = await api.getClass(classId);
      return ClassResponseSchema.parse(data);
    },
    enabled: Boolean(classId),
  });
}

export function useCreateClass() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateClassInput) => {
      const parsed = CreateClassRequestSchema.parse(payload);
      const result = await api.createClass(parsed);
      return ClassResponseSchema.parse(result);
    },
    onSuccess: async (newClass) => {
      // Optimistically update the cache
      queryClient.setQueryData(
        classesQueryKey,
        (current: PaginatedClasses | undefined) => {
          if (!current) {
            return current;
          }
          const existingItems = Array.isArray(current.items) ? current.items : [];
          const filtered = existingItems.filter((item) => item.id !== newClass.id);
          return {
            ...current,
            items: [newClass, ...filtered],
          };
        }
      );
      await queryClient.refetchQueries({ queryKey: classesQueryKey });
    },
  });
}

export function useUpdateClass() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateClassInput }) => {
      const parsed = UpdateClassRequestSchema.parse(payload);
      const result = await api.updateClass(id, parsed);
      return ClassResponseSchema.parse(result);
    },
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: classesQueryKey }),
        queryClient.refetchQueries({ queryKey: classQueryKey(variables.id) }),
      ]);
    },
  });
}

export function useDeleteClassForever() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.deleteClassForever(id);
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: classesQueryKey });
    },
  });
}

export function useStudents(classId: string | undefined) {
  const api = useApiClient();
  return useQuery<PaginatedStudents>({
    queryKey: classId ? studentsQueryKey(classId) : ["students"],
    queryFn: async () => {
      if (!classId) {
        throw new Error("classId required");
      }
      const data = await api.listStudents(classId, { limit: 100 });
      return ListStudentsResponseSchema.parse(data);
    },
    enabled: Boolean(classId),
  });
}

export function useStudent(classId: string | undefined, studentId: string | undefined) {
  const api = useApiClient();
  return useQuery<Student>({
    queryKey:
      classId && studentId ? studentQueryKey(classId, studentId) : ["student"],
    queryFn: async () => {
      if (!classId || !studentId) {
        throw new Error("classId and studentId required");
      }
      const data = await api.getStudent(classId, studentId);
      return StudentResponseSchema.parse(data);
    },
    enabled: Boolean(classId && studentId),
  });
}

export function useCreateStudent(classId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateStudentInput) => {
      const parsed = CreateStudentRequestSchema.parse(payload);
      const result = await api.createStudent(classId, parsed);
      return StudentResponseSchema.parse(result);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: studentsQueryKey(classId) }),
        // Refetch assignments to update student submission counts
        queryClient.refetchQueries({ queryKey: assignmentsQueryKey }),
        // Also refetch individual assignment queries (for detail pages)
        queryClient.refetchQueries({ queryKey: ["assignment"] }),
      ]);
    },
  });
}

export function useUpdateStudent(classId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      studentId,
      payload,
    }: {
      studentId: string;
      payload: UpdateStudentInput;
    }) => {
      const parsed = UpdateStudentRequestSchema.parse(payload);
      const result = await api.updateStudent(classId, studentId, parsed);
      return StudentResponseSchema.parse(result);
    },
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: studentsQueryKey(classId) }),
        queryClient.refetchQueries({
          queryKey: studentQueryKey(classId, variables.studentId),
        }),
        // Refetch assignments to update student submission counts (e.g., when archiving)
        queryClient.refetchQueries({ queryKey: assignmentsQueryKey }),
        queryClient.refetchQueries({ queryKey: ["assignment"] }),
      ]);
    },
  });
}

export function useDeleteStudent(classId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (studentId: string) => {
      await api.deleteStudent(classId, studentId);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: studentsQueryKey(classId) }),
        // Refetch assignments to update student submission counts
        queryClient.refetchQueries({ queryKey: assignmentsQueryKey }),
        // Also refetch individual assignment queries (for detail pages)
        queryClient.refetchQueries({ queryKey: ["assignment"] }),
      ]);
    },
  });
}

export function useDeleteStudentForever(classId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (studentId: string) => {
      await api.deleteStudentForever(classId, studentId);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: studentsQueryKey(classId) }),
        // Refetch assignments to update student submission counts
        queryClient.refetchQueries({ queryKey: assignmentsQueryKey }),
        // Also refetch individual assignment queries (for detail pages)
        queryClient.refetchQueries({ queryKey: ["assignment"] }),
      ]);
    },
  });
}

export function useUpdateUserPreferences() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { preferences: Record<string, unknown> }) => {
      return await api.updateUserPreferences(payload.preferences);
    },
    onSuccess: (updatedUser) => {
      // Update React Query cache
      queryClient.setQueryData(["current-user"], updatedUser);

      // Sync Zustand auth store with updated user data
      const { setUser } = useAuthStore.getState();
      setUser(updatedUser);
    },
  });
}

export function useUpdatePreferredName() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (preferredName: string | null): Promise<AuthUser> => {
      return await api.updatePreferredName(preferredName);
    },
    onSuccess: (updatedUser: AuthUser) => {
      // Update React Query cache
      queryClient.setQueryData(["current-user"], updatedUser);

      // Sync Zustand auth store with updated user data
      const { setUser } = useAuthStore.getState();
      setUser(updatedUser);
    },
  });
}

export function useUpdateStudentWritingEnabled() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (enabled: boolean): Promise<AuthUser> => {
      return await api.updateStudentWritingEnabled(enabled);
    },
    onSuccess: (updatedUser: AuthUser) => {
      // Update React Query cache
      queryClient.setQueryData(["current-user"], updatedUser);

      // Sync Zustand auth store with updated user data
      const { setUser } = useAuthStore.getState();
      setUser(updatedUser);
    },
  });
}

export function useDeleteAccount() {
  const api = useApiClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      await api.deleteAccount();
    },
  });
}

// CriteriaTemplate Template Hooks
const criteriaTemplatesQueryKey = ["criteriaTemplates"] as const;
const criteriaTemplateQueryKey = (criteriaTemplateId: string) => ["criteriaTemplate", criteriaTemplateId] as const;

export function useCriteriaTemplates(params?: {
  paperType?: PaperType | null;
  grades?: number[] | null;
  includeArchived?: boolean;
}) {
  const api = useApiClient();
  return useQuery<CriteriaTemplateList>({
    queryKey: [...criteriaTemplatesQueryKey, params],
    queryFn: async () => {
      const data = await api.listCriteriaTemplates(params);
      return CriteriaTemplateListSchema.parse(data);
    },
  });
}

export function useCriteriaTemplate(criteriaTemplateId: string | undefined) {
  const api = useApiClient();
  return useQuery<CriteriaTemplateWithCriteria>({
    queryKey: criteriaTemplateId ? criteriaTemplateQueryKey(criteriaTemplateId) : ["criteriaTemplate"],
    queryFn: async () => {
      if (!criteriaTemplateId) {
        throw new Error("criteriaTemplateId required");
      }
      const data = await api.getCriteriaTemplate(criteriaTemplateId);
      return CriteriaTemplateWithCriteriaSchema.parse(data);
    },
    enabled: Boolean(criteriaTemplateId),
  });
}

export function useCreateCriteriaTemplate() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateCriteriaTemplateInput) => {
      const parsed = CreateCriteriaTemplateInputSchema.parse(payload);
      const result = await api.createCriteriaTemplate(parsed);
      return CriteriaTemplateWithCriteriaSchema.parse(result);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: criteriaTemplatesQueryKey }),
        queryClient.refetchQueries({ queryKey: templatesQueryKey }),
      ]);
    },
  });
}

export function useUpdateCriteriaTemplate() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateCriteriaTemplateInput }) => {
      const parsed = UpdateCriteriaTemplateInputSchema.parse(payload);
      const result = await api.updateCriteriaTemplate(id, parsed);
      return CriteriaTemplateSchema.parse(result);
    },
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: criteriaTemplatesQueryKey }),
        queryClient.refetchQueries({ queryKey: criteriaTemplateQueryKey(variables.id) }),
        queryClient.refetchQueries({ queryKey: templatesQueryKey }),
      ]);
    },
  });
}

export function useDeleteCriteriaTemplate() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.deleteCriteriaTemplate(id);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: criteriaTemplatesQueryKey }),
        queryClient.refetchQueries({ queryKey: templatesQueryKey }),
      ]);
    },
  });
}

export function useApplyCriteriaTemplateToAssignment(assignmentId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ApplyCriteriaTemplateInput) => {
      const parsed = ApplyCriteriaTemplateInputSchema.parse(payload);
      const result = await api.applyCriteriaTemplateToAssignment(assignmentId, parsed);
      return AssignmentCriteriaSchema.parse(result);
    },
    onSuccess: async () => {
      await Promise.all([
        // Refetch assignment to get updated criteria
        queryClient.refetchQueries({ queryKey: assignmentQueryKey(assignmentId) }),
        queryClient.refetchQueries({ queryKey: assignmentsQueryKey }),
        // Documents may need regrade due to criteria change
        queryClient.refetchQueries({ queryKey: documentsQueryKey(assignmentId) }),
      ]);
    },
  });
}

export function useSaveAssignmentCriteriaAsCriteriaTemplate(assignmentId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SaveAsCriteriaTemplateInput) => {
      const parsed = SaveAsCriteriaTemplateInputSchema.parse(payload);
      const result = await api.saveAssignmentCriteriaAsCriteriaTemplate(assignmentId, parsed);
      return CriteriaTemplateWithCriteriaSchema.parse(result);
    },
    onSuccess: async () => {
      await Promise.all([
        // Refetch criteriaTemplates list to include newly saved criteria template
        queryClient.refetchQueries({ queryKey: criteriaTemplatesQueryKey }),
        // Refetch templates list
        queryClient.refetchQueries({ queryKey: templatesQueryKey }),
        // Refetch assignment to update criteriaAreCustom flag (now matches a template)
        queryClient.refetchQueries({ queryKey: assignmentQueryKey(assignmentId) }),
        queryClient.refetchQueries({ queryKey: assignmentsQueryKey }),
      ]);
    },
  });
}

// Template Hooks (for combined starter + user templates)
const templatesQueryKey = ["templates"] as const;

export function useAllTemplates(grade?: number) {
  const api = useApiClient();
  return useQuery({
    queryKey: [...templatesQueryKey, { grade }],
    queryFn: async () => {
      return await api.listAllTemplates(grade);
    },
  });
}

export function useDuplicateCriteriaTemplate() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await api.duplicateCriteriaTemplate(id);
      return CriteriaTemplateWithCriteriaSchema.parse(result);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: criteriaTemplatesQueryKey }),
        queryClient.refetchQueries({ queryKey: templatesQueryKey }),
      ]);
    },
  });
}

// CriteriaTemplate Criterion CRUD Hooks
export function useCreateCriteriaTemplateItem(criteriaTemplateId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { label: string; llmDescription: string; humanDescription: string; category?: string }) => {
      return await api.createCriteriaTemplateItem(criteriaTemplateId, payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: criteriaTemplateQueryKey(criteriaTemplateId) });
      await queryClient.invalidateQueries({ queryKey: criteriaTemplatesQueryKey });
      await queryClient.invalidateQueries({ queryKey: templatesQueryKey });
    },
  });
}

export function useUpdateCriteriaTemplateItem(criteriaTemplateId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      criterionKey: string;
      label?: string;
      llmDescription?: string;
      humanDescription?: string;
      category?: string;
    }) => {
      const { criterionKey, ...rest } = payload;
      return await api.updateCriteriaTemplateItem(criteriaTemplateId, criterionKey, rest);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: criteriaTemplateQueryKey(criteriaTemplateId) });
      await queryClient.invalidateQueries({ queryKey: criteriaTemplatesQueryKey });
      await queryClient.invalidateQueries({ queryKey: templatesQueryKey });
    },
  });
}

export function useDeleteCriteriaTemplateItem(criteriaTemplateId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: async (criterionKey: string) => {
      await api.deleteCriteriaTemplateItem(criteriaTemplateId, criterionKey);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: criteriaTemplateQueryKey(criteriaTemplateId) });
      await queryClient.invalidateQueries({ queryKey: criteriaTemplatesQueryKey });
      await queryClient.invalidateQueries({ queryKey: templatesQueryKey });
    },
  });
}

export function useGenerateCriteriaTemplateItems(criteriaTemplateId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { prompt: string; gradeLevel?: number | null }) => {
      return await api.generateCriteriaTemplateItems(criteriaTemplateId, payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: criteriaTemplateQueryKey(criteriaTemplateId) });
      await queryClient.invalidateQueries({ queryKey: criteriaTemplatesQueryKey });
      await queryClient.invalidateQueries({ queryKey: templatesQueryKey });
    },
  });
}

// Vocabulary Preferences Hooks
const vocabularyQueryKey = ["vocabulary"] as const;
const gradeVocabularyQueryKey = (grade: string) => ["vocabulary", grade] as const;

export function useVocabularyPreferences() {
  const api = useApiClient();
  return useQuery<AllGradesVocabularyResponse>({
    queryKey: vocabularyQueryKey,
    queryFn: async () => {
      const data = await api.getAllVocabulary();
      return data as AllGradesVocabularyResponse;
    },
  });
}

export function useGradeVocabulary(grade: string) {
  const api = useApiClient();
  return useQuery<GradeVocabularyResponse>({
    queryKey: gradeVocabularyQueryKey(grade),
    queryFn: async () => {
      const data = await api.getGradeVocabulary(grade);
      return data as GradeVocabularyResponse;
    },
  });
}

export function useUpdateVocabularyForGrade(grade: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<GradeVocabularyResponse, unknown, UpdateGradeVocabularyRequest>({
    mutationFn: async (payload: UpdateGradeVocabularyRequest) => {
      const data = await api.updateGradeVocabulary(grade, payload);
      return data as GradeVocabularyResponse;
    },
    onSuccess: (updatedGrade) => {
      // Update single grade cache
      queryClient.setQueryData(gradeVocabularyQueryKey(grade), updatedGrade);

      // Update all grades cache
      queryClient.setQueryData(
        vocabularyQueryKey,
        (current: AllGradesVocabularyResponse | undefined) => {
          if (!current) return current;
          return {
            ...current,
            vocabulary: {
              ...current.vocabulary,
              [grade]: updatedGrade,
            },
          };
        }
      );
    },
  });
}

export function useResetVocabularyForGrade(grade: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<GradeVocabularyResponse>({
    mutationFn: async () => {
      const data = await api.resetGradeVocabulary(grade);
      return data as GradeVocabularyResponse;
    },
    onSuccess: (resetGrade) => {
      // Update single grade cache
      queryClient.setQueryData(gradeVocabularyQueryKey(grade), resetGrade);

      // Update all grades cache
      queryClient.setQueryData(
        vocabularyQueryKey,
        (current: AllGradesVocabularyResponse | undefined) => {
          if (!current) return current;
          return {
            ...current,
            vocabulary: {
              ...current.vocabulary,
              [grade]: resetGrade,
            },
          };
        }
      );
    },
  });
}

export function useResetAllVocabulary() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<AllGradesVocabularyResponse>({
    mutationFn: async () => {
      const data = await api.resetAllVocabulary();
      return data as AllGradesVocabularyResponse;
    },
    onSuccess: (resetData) => {
      // Update all grades cache
      queryClient.setQueryData(vocabularyQueryKey, resetData);

      // Update individual grade caches
      Object.entries(resetData.vocabulary).forEach(([grade, gradeData]) => {
        queryClient.setQueryData(gradeVocabularyQueryKey(grade), gradeData);
      });
    },
  });
}

// Email Template Hooks (user-level)
const emailTemplatesQueryKey = ["email-templates"] as const;
const emailTemplateQueryKey = (templateId: string) => ["email-template", templateId] as const;

export function useEmailTemplates() {
  const api = useApiClient();
  return useQuery<EmailTemplateList>({
    queryKey: emailTemplatesQueryKey,
    queryFn: async () => {
      const data = await api.listEmailTemplates();
      return EmailTemplateListSchema.parse(data);
    },
  });
}

export function useEmailTemplate(templateId: string | undefined) {
  const api = useApiClient();
  return useQuery<EmailTemplate>({
    queryKey: templateId ? emailTemplateQueryKey(templateId) : ["email-template"],
    queryFn: async () => {
      if (!templateId) {
        throw new Error("templateId required");
      }
      const data = await api.getEmailTemplate(templateId);
      return EmailTemplateSchema.parse(data);
    },
    enabled: Boolean(templateId),
  });
}

export function useCreateEmailTemplate() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateEmailTemplateInput) => {
      const parsed = CreateEmailTemplateInputSchema.parse(payload);
      const result = await api.createEmailTemplate(parsed);
      return EmailTemplateSchema.parse(result);
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: emailTemplatesQueryKey });
    },
  });
}

export function useUpdateEmailTemplate() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      templateId,
      payload,
    }: {
      templateId: string;
      payload: UpdateEmailTemplateInput;
    }) => {
      const parsed = UpdateEmailTemplateInputSchema.parse(payload);
      const result = await api.updateEmailTemplate(templateId, parsed);
      return EmailTemplateSchema.parse(result);
    },
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: emailTemplatesQueryKey }),
        queryClient.refetchQueries({
          queryKey: emailTemplateQueryKey(variables.templateId),
        }),
      ]);
    },
  });
}

export function useDeleteEmailTemplate() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (templateId: string) => {
      await api.deleteEmailTemplate(templateId);
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: emailTemplatesQueryKey });
    },
  });
}

export function useRenderDocumentEmail(assignmentId: string) {
  const api = useApiClient();
  return useMutation<RenderEmailResponse, unknown, { documentId: string; templateId?: string }>({
    mutationFn: async ({ documentId, templateId }) => {
      const result = await api.renderDocumentEmail(assignmentId, documentId, templateId);
      return RenderEmailResponseSchema.parse(result);
    },
  });
}

// =============================================================================
// Google Classroom Hooks
// =============================================================================

export interface GCStatus {
  gc_mode: boolean;
  gc_mode_enabled_at: string | null;
  has_refresh_token: boolean;
  has_write_scopes: boolean;
  has_ejected_data: boolean;
}

const gcStatusQueryKey = ["gc-status"] as const;

export function useGCStatus(options?: { enabled?: boolean }) {
  const api = useApiClient();
  return useQuery<GCStatus>({
    queryKey: gcStatusQueryKey,
    queryFn: async () => {
      const data = await api.getGCStatus();
      return data;
    },
    staleTime: 30_000, // Cache for 30 seconds
    enabled: options?.enabled ?? true,
  });
}

export function useDisconnectGC() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      return await api.disconnectGC();
    },
    onSuccess: async () => {
      await Promise.all([
        // Refresh GC status
        queryClient.refetchQueries({ queryKey: gcStatusQueryKey }),
        // Refresh classes, students, assignments since they may have changed
        queryClient.refetchQueries({ queryKey: classesQueryKey }),
        queryClient.refetchQueries({ queryKey: assignmentsQueryKey }),
      ]);
    },
  });
}

// Phase 2: Course Import Hooks
const gcCoursesQueryKey = ["gc-courses"] as const;

// Re-export types for components that import from hooks
export type { GCCourse, GCClassOut, GCSyncResult };

export function useGCCourses(options?: { enabled?: boolean }) {
  const api = useApiClient();
  return useQuery<GCCourse[]>({
    queryKey: gcCoursesQueryKey,
    queryFn: async () => {
      return await api.listGCCourses();
    },
    staleTime: 60_000, // Cache for 1 minute
    enabled: options?.enabled ?? true,
  });
}

export function useImportGCCourse() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<GCClassOut, unknown, string>({
    mutationFn: async (courseId: string) => {
      return await api.importGCCourse(courseId);
    },
    onSuccess: async () => {
      await Promise.all([
        // Refresh courses list (to update already_imported flags)
        queryClient.refetchQueries({ queryKey: gcCoursesQueryKey }),
        // Refresh classes list (new class added)
        queryClient.refetchQueries({ queryKey: classesQueryKey }),
      ]);
    },
  });
}

// Phase 3: Sync Class Hook
// NOTE: No onSuccess refetches here — callers are responsible for invalidation.
// useGCGlobalAutoSync batches refetches after ALL syncs complete.
// SyncButton triggers its own refetches for manual sync.
export function useSyncClass() {
  const api = useApiClient();
  return useMutation<GCSyncResult, Error, string>({
    mutationFn: async (classId: string) => {
      return await api.syncClass(classId);
    },
  });
}

/** Refetch all queries that may be stale after a GC sync. */
export function useRefetchAfterSync() {
  const queryClient = useQueryClient();
  return async (classIds: string[]) => {
    await Promise.all([
      queryClient.refetchQueries({ queryKey: classesQueryKey }),
      queryClient.refetchQueries({ queryKey: assignmentsQueryKey }),
      queryClient.refetchQueries({ queryKey: ["assignment-documents"] }),
      ...classIds.map((id) =>
        Promise.all([
          queryClient.refetchQueries({ queryKey: classQueryKey(id) }),
          queryClient.refetchQueries({ queryKey: studentsQueryKey(id) }),
        ])
      ),
    ]);
  };
}

// =============================================================================
// Student Portal Hooks (public - no auth required)
// =============================================================================

const portalQueryKey = (portalKey: string) => ["portal", portalKey] as const;
const portalPaperQueryKey = (portalKey: string, documentId: string) =>
  ["portal", portalKey, "paper", documentId] as const;
const portalPaperProgressionQueryKey = (portalKey: string, documentId: string) =>
  ["portal", portalKey, "paper", documentId, "progression"] as const;

export function useStudentPortal(portalKey: string | undefined) {
  const api = useApiClient();
  return useQuery<StudentPortalResponse>({
    queryKey: portalKey ? portalQueryKey(portalKey) : ["portal"],
    queryFn: async () => {
      if (!portalKey) {
        throw new Error("portalKey required");
      }
      const response = await api.getStudentPortal(portalKey);
      const parsed: StudentPortalResponse = StudentPortalResponseSchema.parse(response);
      return parsed;
    },
    enabled: Boolean(portalKey),
    retry: (failureCount, error: unknown) => {
      const status = (error as { status?: number } | undefined)?.status;
      if (status && [404, 410].includes(status)) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

export function usePortalPaper(portalKey: string | undefined, documentId: string | undefined) {
  const api = useApiClient();
  return useQuery<ShareDocument>({
    queryKey:
      portalKey && documentId
        ? portalPaperQueryKey(portalKey, documentId)
        : ["portal-paper"],
    queryFn: async () => {
      if (!portalKey || !documentId) {
        throw new Error("portalKey and documentId required");
      }
      const response = await api.getPortalPaper(portalKey, documentId);
      const parsed: ShareDocument = ShareDocumentSchema.parse(response);
      return parsed;
    },
    enabled: Boolean(portalKey && documentId),
    retry: (failureCount, error: unknown) => {
      const status = (error as { status?: number } | undefined)?.status;
      if (status && [404, 410].includes(status)) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

export function usePortalPaperProgression(
  portalKey: string | undefined,
  documentId: string | undefined
) {
  const api = useApiClient();
  return useQuery<PortalProgressionResponse>({
    queryKey:
      portalKey && documentId
        ? portalPaperProgressionQueryKey(portalKey, documentId)
        : ["portal-paper-progression"],
    queryFn: async () => {
      if (!portalKey || !documentId) {
        throw new Error("portalKey and documentId required");
      }
      const response = await api.getPortalPaperProgression(portalKey, documentId);
      const parsed: PortalProgressionResponse = PortalProgressionResponseSchema.parse(response);
      return parsed;
    },
    enabled: Boolean(portalKey && documentId),
    retry: (failureCount, error: unknown) => {
      const status = (error as { status?: number } | undefined)?.status;
      if (status && [404, 410].includes(status)) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

export function useUpdatePortalCompletedHighlights(
  portalKey: string,
  documentId: string
) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const paperQueryKey = portalPaperQueryKey(portalKey, documentId);

  return useMutation<ShareDocument, unknown, UpdateCompletedHighlightsInput>({
    mutationFn: async (payload: UpdateCompletedHighlightsInput) => {
      const result = await api.updatePortalCompletedHighlights(portalKey, documentId, payload);
      return result;
    },
    onMutate: async () => {
      // Cancel outgoing refetches to prevent race conditions
      await queryClient.cancelQueries({ queryKey: paperQueryKey });
    },
    onSuccess: (data) => {
      // Update cache with server response
      queryClient.setQueryData<ShareDocument>(paperQueryKey, data);
    },
  });
}

// =============================================================================
// Portal Writing Mode Hooks (public - no auth required)
// =============================================================================

const portalAssignmentWritingQueryKey = (portalKey: string, assignmentId: string) =>
  ["portal", portalKey, "assignment", assignmentId, "writing"] as const;

export function usePortalAssignmentForWriting(
  portalKey: string | undefined,
  assignmentId: string | undefined
) {
  const api = useApiClient();
  return useQuery<PortalAssignmentWritingResponse>({
    queryKey:
      portalKey && assignmentId
        ? portalAssignmentWritingQueryKey(portalKey, assignmentId)
        : ["portal-assignment-writing"],
    queryFn: async () => {
      if (!portalKey || !assignmentId) {
        throw new Error("portalKey and assignmentId required");
      }
      const response = await api.getPortalAssignmentForWriting(portalKey, assignmentId);
      return PortalAssignmentWritingResponseSchema.parse(response);
    },
    enabled: Boolean(portalKey && assignmentId),
    retry: (failureCount, error: unknown) => {
      const status = (error as { status?: number } | undefined)?.status;
      if (status && [403, 404, 410].includes(status)) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

export function useCreatePortalDocument(portalKey: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<PortalCreateDocumentResponse, unknown, string>({
    mutationFn: async (assignmentId: string) => {
      const result = await api.createPortalDocumentForWriting(portalKey, assignmentId);
      return PortalCreateDocumentResponseSchema.parse(result);
    },
    onSuccess: (data, assignmentId) => {
      // Invalidate the portal query to update assignment card state
      void queryClient.invalidateQueries({ queryKey: portalQueryKey(portalKey) });
      // Invalidate the writing assignment query
      void queryClient.invalidateQueries({
        queryKey: portalAssignmentWritingQueryKey(portalKey, assignmentId),
      });
    },
  });
}

export function useUpdatePortalVersionContent(
  portalKey: string,
  documentId: string,
  versionId: string,
  assignmentId: string
) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<PortalDocumentVersion, unknown, PortalVersionUpdateInput>({
    mutationFn: async (payload: PortalVersionUpdateInput) => {
      const result = await api.updatePortalVersionContent(
        portalKey,
        documentId,
        versionId,
        payload
      );
      return PortalDocumentVersionSchema.parse(result);
    },
    onSuccess: () => {
      // Invalidate the writing query cache so navigating back shows fresh data
      void queryClient.invalidateQueries({
        queryKey: portalAssignmentWritingQueryKey(portalKey, assignmentId),
      });
    },
  });
}

export function useSubmitPortalVersion(
  portalKey: string,
  documentId: string
) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<PortalVersionSubmitResponse, unknown, string>({
    mutationFn: async (versionId: string) => {
      const result = await api.submitPortalVersion(portalKey, documentId, versionId);
      return PortalVersionSubmitResponseSchema.parse(result);
    },
    onSuccess: () => {
      // Invalidate portal query to update assignment status
      void queryClient.invalidateQueries({ queryKey: portalQueryKey(portalKey) });
    },
  });
}

export function useCreatePortalVersion(
  portalKey: string,
  documentId: string
) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<
    PortalDocumentVersion,
    unknown,
    { parentVersionId?: string; bodyJson?: object }
  >({
    mutationFn: async (payload) => {
      const result = await api.createPortalVersion(
        portalKey,
        documentId,
        payload
      );
      return PortalDocumentVersionSchema.parse(result);
    },
    onSuccess: () => {
      // Invalidate portal query to update version status
      void queryClient.invalidateQueries({ queryKey: portalQueryKey(portalKey) });
    },
  });
}

// Student Portal Key Management Hooks (authenticated - teacher endpoints)
export function useRegenerateStudentPortalKey(classId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Student, unknown, string>({
    mutationFn: async (studentId: string) => {
      const result = await api.regenerateStudentPortalKey(classId, studentId);
      return result;
    },
    onSuccess: async (_, studentId) => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: studentsQueryKey(classId) }),
        queryClient.refetchQueries({
          queryKey: studentQueryKey(classId, studentId),
        }),
      ]);
    },
  });
}

export function useRevokeStudentPortalKey(classId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Student, unknown, string>({
    mutationFn: async (studentId: string) => {
      const result = await api.revokeStudentPortalKey(classId, studentId);
      return result;
    },
    onSuccess: async (_, studentId) => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: studentsQueryKey(classId) }),
        queryClient.refetchQueries({
          queryKey: studentQueryKey(classId, studentId),
        }),
      ]);
    },
  });
}

// =============================================================================
// Edit Metrics Hooks (teacher view)
// =============================================================================

const editMetricsQueryKey = (assignmentId: string, documentId: string) =>
  ["edit-metrics", assignmentId, documentId] as const;

export function useDocumentEditMetrics(
  assignmentId: string | undefined,
  documentId: string | undefined
) {
  const api = useApiClient();
  return useQuery<EditMetricsResponse>({
    queryKey:
      assignmentId && documentId
        ? editMetricsQueryKey(assignmentId, documentId)
        : ["edit-metrics"],
    queryFn: async (): Promise<EditMetricsResponse> => {
      if (!assignmentId || !documentId) {
        throw new Error("assignmentId and documentId required");
      }
      return await api.getDocumentEditMetrics(assignmentId, documentId);
    },
    enabled: Boolean(assignmentId && documentId),
    staleTime: 60_000, // Cache for 1 minute
    retry: (failureCount, error: unknown) => {
      const status = (error as { status?: number } | undefined)?.status;
      // Don't retry on 404 (no version/metrics available)
      if (status === 404) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

const editEventsQueryKey = (assignmentId: string, documentId: string) =>
  ["edit-events", assignmentId, documentId] as const;

export function useDocumentEditEvents(
  assignmentId: string | undefined,
  documentId: string | undefined
) {
  const api = useApiClient();
  return useQuery<ExtensionEditEventsResponse>({
    queryKey:
      assignmentId && documentId
        ? editEventsQueryKey(assignmentId, documentId)
        : ["edit-events"],
    queryFn: async (): Promise<ExtensionEditEventsResponse> => {
      if (!assignmentId || !documentId) {
        throw new Error("assignmentId and documentId required");
      }
      return await api.getDocumentEditEvents(assignmentId, documentId);
    },
    enabled: Boolean(assignmentId && documentId),
    staleTime: 60_000, // Cache for 1 minute
    retry: (failureCount, error: unknown) => {
      const status = (error as { status?: number } | undefined)?.status;
      // Don't retry on 404 (no version/events available)
      if (status === 404) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

// =============================================================================
// Extension Documents Hooks (AI Detector)
// =============================================================================

const extensionDocumentsQueryKey = ["extension-documents"] as const;
const extensionDocumentQueryKey = (documentId: string) =>
  ["extension-document", documentId] as const;
const extensionDocumentEventsQueryKey = (documentId: string) =>
  ["extension-document-events", documentId] as const;
const extensionLicenseQueryKey = ["extension-license"] as const;

export function useExtensionDocuments(params?: { limit?: number; offset?: number }) {
  const api = useApiClient();
  return useQuery<ExtensionDocumentListResponse>({
    queryKey: [...extensionDocumentsQueryKey, params],
    queryFn: async () => {
      return await api.listExtensionDocuments(params);
    },
    staleTime: 30_000, // Cache for 30 seconds
  });
}

export function useExtensionDocument(documentId: string | undefined) {
  const api = useApiClient();
  return useQuery<ExtensionDocumentDetailResponse>({
    queryKey: documentId ? extensionDocumentQueryKey(documentId) : ["extension-document"],
    queryFn: async () => {
      if (!documentId) {
        throw new Error("documentId required");
      }
      return await api.getExtensionDocument(documentId);
    },
    enabled: Boolean(documentId),
    staleTime: 60_000, // Cache for 1 minute
    retry: (failureCount, error: unknown) => {
      const status = (error as { status?: number } | undefined)?.status;
      if (status === 404) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

export function useExtensionDocumentEvents(documentId: string | undefined) {
  const api = useApiClient();
  return useQuery<ExtensionEditEventsResponse>({
    queryKey: documentId ? extensionDocumentEventsQueryKey(documentId) : ["extension-document-events"],
    queryFn: async () => {
      if (!documentId) {
        throw new Error("documentId required");
      }
      return await api.getExtensionDocumentEvents(documentId);
    },
    enabled: Boolean(documentId),
    staleTime: 60_000, // Cache for 1 minute
    retry: (failureCount, error: unknown) => {
      const status = (error as { status?: number } | undefined)?.status;
      if (status === 404) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

export function useDeleteExtensionDocument() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (documentId: string) => {
      await api.deleteExtensionDocument(documentId);
    },
    onSuccess: async () => {
      // Invalidate the documents list
      await queryClient.invalidateQueries({ queryKey: extensionDocumentsQueryKey });
    },
  });
}

export function useDeleteAllExtensionDocuments() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      return await api.deleteAllExtensionDocuments();
    },
    onSuccess: async () => {
      // Invalidate the documents list and license status (quota changes)
      await queryClient.invalidateQueries({ queryKey: extensionDocumentsQueryKey });
      await queryClient.invalidateQueries({ queryKey: extensionLicenseQueryKey });
    },
  });
}

export function useExportExtensionData() {
  const api = useApiClient();
  return useMutation({
    mutationFn: async () => {
      return await api.exportExtensionData();
    },
  });
}

export function useExtensionLicenseStatus() {
  const api = useApiClient();
  return useQuery<ExtensionLicenseStatusResponse>({
    queryKey: extensionLicenseQueryKey,
    queryFn: async () => {
      return await api.getExtensionLicenseStatus();
    },
    staleTime: 60_000, // Cache for 1 minute
  });
}
